import { ExcelButton } from "@/controls/buttons/Excel-button";
import { ATHENS_GRAY } from "@/controls/shared/constants/colors";
import {
	ErrorModal,
	HelmetDexteel,
	getMomentTz,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { BarChart, ViewList } from "@mui/icons-material";
import {
	Button,
	ButtonGroup,
	Grid2 as Grid,
	Paper,
	Typography,
} from "@mui/material";
import { ProcessCellForExportParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";
import { getFailedStartup } from "../repositories/ProductionRepository";
import { FailedStartupGroupedTable } from "./components/FailedStartupGroupedTable";
import { FailedStartupTable } from "./components/FailedStartupTable";
import { Filters } from "./components/Filters";
import { FailedStartupGrouped } from "./models/FailedStartupHeat";

const moment = getMomentTz();

type ViewMode = "detailed" | "grouped";

interface FiltersType {
	fromDate: Date;
	toDate: Date;
	shift: string | null;
	crew: string | null;
}

export const FailedStartupPage = () => {
	const gridRef = useRef<AgGridReact>(null);
	const { shiftInfo } = useShiftNavigator();
	const shiftDate = moment(shiftInfo?.CurrentStart).format("MM-DD-YYYY");

	const [heats, setHeats] = useState<any[]>([]);
	const [groupedData, setGroupedData] = useState<FailedStartupGrouped[]>([]);
	const [viewMode, setViewMode] = useState<ViewMode>("detailed");
	const [filters, setFilters] = useState<FiltersType>({
		fromDate: moment.utc().subtract(1, "month").toDate(),
		toDate: moment.utc().toDate(),
		shift: "All",
		crew: "All",
	});
	const [error, setError] = useState("");

	const getFailedStartupHeats = async () => {
		const resp = await getFailedStartup({
			...filters,
			shift: filters.shift === "All" ? null : filters.shift,
			crew: filters.crew === "All" ? null : filters.crew,
		});
		if (resp.ok) {
			// First table contains detailed data
			setHeats(resp.data.tables[0].rows);
			// Second table contains grouped data
			if (resp.data.tables.length > 1) {
				setGroupedData(resp.data.tables[1].rows);
			}
		} else {
			setError(resp.message);
		}
	};

	useEffect(() => {
		getFailedStartupHeats();
	}, [filters]);

	const handleExportToExcel = () => {
		if (gridRef.current && gridRef.current.api) {
			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: `Failed-Startup-${viewMode === "grouped" ? "Grouped" : "Detailed"}-${shiftDate}.xlsx`,
				sheetName:
					viewMode === "grouped" ? "Failed Startup Grouped" : "Failed Startup",
				processCellCallback: (params: ProcessCellForExportParams) => {
					const val = params.value;
					return val ?? "";
				},
			};
			gridRef.current.api.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	return (
		<>
			<HelmetDexteel
				title={`Failed Startup ${`- ` + viewMode[0].toUpperCase() + viewMode.slice(1)}`}
			/>
			<Paper
				elevation={0}
				style={{
					width: "100%",
					padding: "20px 20px",
					height: "93vh",
					backgroundColor: ATHENS_GRAY,
				}}
			>
				<Grid container justifyContent="center" alignItems="center">
					<Grid size={{ xs: 12, md: 12 }} container spacing={1}>
						<Grid size={{ xs: 12 }}>
							<Typography variant="h5" style={{ fontWeight: 600 }}>
								Failed Startups
							</Typography>
						</Grid>
						<Grid size={{ xs: 12 }} style={{ marginBottom: "10px" }}>
							<Grid
								container
								direction="row"
								justifyContent="flex-start"
								alignItems="stretch"
								spacing={1}
							>
								<Grid size={{ xs: "auto" }} style={{ display: "flex" }}>
									<Filters
										onFilterChange={(fromDate, toDate, shift, crew) =>
											setFilters({ fromDate, toDate, shift, crew })
										}
										initialFromDate={filters.fromDate}
										initialToDate={filters.toDate}
										filterShift={filters.shift}
										onFilterShiftChange={(shift) =>
											setFilters((prev) => ({ ...prev, shift }))
										}
										filterCrew={filters.crew}
										onFilterCrewChange={(crew) =>
											setFilters((prev) => ({ ...prev, crew }))
										}
									/>
								</Grid>
								<Grid size={{ xs: "auto" }} style={{ display: "flex" }}>
									<Paper
										elevation={1}
										sx={{
											display: "inline-flex",
											alignItems: "center",
											p: 1,
										}}
									>
										<ButtonGroup
											variant="outlined"
											size="small"
											sx={{
												height: "40px",
												"& .MuiButton-root": {
													textTransform: "none",
													minWidth: "110px",
													height: "40px",
												},
											}}
										>
											<Button
												variant={
													viewMode === "detailed" ? "contained" : "outlined"
												}
												onClick={() => setViewMode("detailed")}
												startIcon={<ViewList />}
											>
												Detailed
											</Button>
											<Button
												variant={
													viewMode === "grouped" ? "contained" : "outlined"
												}
												onClick={() => setViewMode("grouped")}
												startIcon={<BarChart />}
											>
												Grouped
											</Button>
										</ButtonGroup>
									</Paper>
								</Grid>
								<Grid
									size={{ xs: "auto" }}
									style={{
										marginLeft: "auto",
										display: "flex",
										alignItems: "center",
									}}
								>
									<ExcelButton onClick={handleExportToExcel} />
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid size={{ xs: 12 }} container spacing={2}>
						{viewMode === "detailed" ? (
							<FailedStartupTable
								rows={heats}
								isLoading={false}
								gridRef={gridRef}
							/>
						) : (
							<FailedStartupGroupedTable
								rows={groupedData}
								isLoading={false}
								gridRef={gridRef}
							/>
						)}
					</Grid>
				</Grid>
			</Paper>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
